import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { Bot, Users, BarChart3 } from "lucide-react";
import SectionHeading from "./SectionHeading";
import AIAgentNetwork from "./AIAgentNetwork";
import { HOW_IT_WORKS } from "@/data/content";

const iconMap = { agents: Bot, humans: Users, growth: BarChart3 };

export const HowItWorks = () => {
    const [active, setActive] = useState(HOW_IT_WORKS[0].id);
    const sectionRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start 70%", "end 30%"],
    });

    // Auto-advance active step based on scroll progress
    useEffect(() => {
        const unsub = scrollYProgress.on("change", (v) => {
            if (v < 0.34) setActive("agents");
            else if (v < 0.67) setActive("humans");
            else setActive("growth");
        });
        return unsub;
    }, [scrollYProgress]);

    const activeStep = HOW_IT_WORKS.find((s) => s.id === active);

    return (
        <section
            id="how-it-works"
            ref={sectionRef}
            className="relative py-24 lg:py-32 overflow-hidden"
            aria-labelledby="how-it-works-heading"
        >
            <div className="absolute inset-0 dot-grid-fine opacity-60 pointer-events-none" />
            <div className="mx-auto max-w-7xl container-px relative">
                <SectionHeading
                    eyebrow="How AI + Human Talent Works"
                    title={
                        <>
                            Agents accelerate. Humans elevate.{" "}
                            <span className="italic text-[hsl(var(--text-muted))]">Systems compound.</span>
                        </>
                    }
                    subtitle="Our operating model in three movements — scroll or click to traverse."
                    className="mb-12"
                />

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
                    {/* Left: narrative steps */}
                    <div className="lg:col-span-6 flex flex-col gap-3">
                        {HOW_IT_WORKS.map((step, i) => {
                            const Icon = iconMap[step.id];
                            const isActive = step.id === active;
                            return (
                                <motion.button
                                    key={step.id}
                                    type="button"
                                    onClick={() => setActive(step.id)}
                                    initial={{ opacity: 0, y: 14 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-10% 0px" }}
                                    transition={{ duration: 0.5, delay: i * 0.06 }}
                                    data-testid={`how-it-works-step-${step.id}`}
                                    className={`text-left rounded-2xl border p-5 sm:p-6 transition-all duration-300 ${
                                        isActive
                                            ? "border-black/15 bg-white shadow-deep"
                                            : "border-black/[0.08] bg-white/60 hover:border-black/15 hover:bg-white"
                                    }`}
                                >
                                    <div className="flex items-start gap-4">
                                        <div
                                            className={`shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-full ring-1 ${
                                                step.accent === "gold"
                                                    ? "bg-[rgba(187,118,33,0.10)] ring-[rgba(187,118,33,0.35)] text-[hsl(var(--gold))]"
                                                    : "bg-[rgba(37,99,235,0.10)] ring-[rgba(37,99,235,0.35)] text-[hsl(var(--blue))]"
                                            }`}
                                        >
                                            <Icon className="h-4 w-4" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between">
                                                <span className="text-[11px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-subtle))]">
                                                    Step {step.step}
                                                </span>
                                                {isActive && (
                                                    <span className="text-[11px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--gold))]">
                                                        — active
                                                    </span>
                                                )}
                                            </div>
                                            <h3 className="mt-1.5 font-display text-2xl lg:text-3xl leading-tight text-[hsl(var(--text))]">
                                                {step.title}
                                            </h3>
                                            <p className="mt-2 text-[14px] text-[hsl(var(--text-muted))] leading-relaxed">
                                                {step.description}
                                            </p>
                                            <div className="mt-3 flex flex-wrap gap-1.5">
                                                {step.chips.map((chip) => (
                                                    <span
                                                        key={chip}
                                                        className="rounded-full bg-[hsl(var(--bg-2))] ring-1 ring-black/[0.08] px-2.5 py-0.5 text-[11px] text-[hsl(var(--text-muted))] font-mono tracking-wide"
                                                    >
                                                        {chip}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </motion.button>
                            );
                        })}
                    </div>

                    {/* Right: sticky animated panel */}
                    <div className="lg:col-span-6 lg:sticky lg:top-28">
                        <div className="relative aspect-[5/4] rounded-3xl border border-black/[0.08] bg-white p-5 sm:p-7 overflow-hidden shadow-deep">
                            <AIAgentNetwork className="absolute inset-0" />
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={activeStep.id}
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -16 }}
                                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                                    className="absolute bottom-5 left-5 right-5 sm:bottom-7 sm:left-7 sm:right-7 rounded-2xl glass-strong p-5 sm:p-6"
                                >
                                    <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[hsl(var(--text-muted))]">
                                        Currently showing
                                    </div>
                                    <div className="font-display text-2xl text-[hsl(var(--text))] mt-1.5">
                                        {activeStep.title}
                                    </div>
                                    <p className="text-[13.5px] text-[hsl(var(--text-muted))] mt-2 max-w-prose">
                                        {activeStep.description}
                                    </p>
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default HowItWorks;
